import { Filter, MessageCircle, Pen, Send } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { Text, View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { getDayJsFromDate } from "../../../core/dayJs";
import { useStores } from "../../../core/stores";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { FormInput } from "../../../ui/FormInput";
import { Group } from "../../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Input } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { LoadingCircular } from "../../../ui/LoadingCircular";
import {
  type BusinessCustomerMailData,
  type BusinessCustomerMailModel,
  BusinessCustomerMailStatus,
} from "./BusinessCustomerMailModel";
import { BusinessPlanImportMailsModal } from "./BusinessPlanImportMailsModal";

type FormData = Omit<BusinessCustomerMailData, "uid"> & { uid?: string };

function isBusinessCustomerMailStatus(
  value: string,
): value is BusinessCustomerMailStatus {
  return Object.values(BusinessCustomerMailStatus).includes(
    value as BusinessCustomerMailStatus,
  );
}

const CustomerEdit = ({
  mailData,
  onSave,
}: {
  mailData: FormData;
  onSave: () => void;
}) => {
  const { businessCustomerMailsStore } = useStores();
  const { businessCustomerMailsApi } = useApi();

  const [status, setStatus] = React.useState<string>(mailData.status);
  const [companyName, setCompanyName] = React.useState(mailData.companyName);
  const [email, setEmail] = React.useState(mailData.email);
  const [currentStep, setCurrentStep] = React.useState(mailData.currentStep);
  const [templateSuffix, setTemplateSuffix] = React.useState(
    mailData.templateSuffix,
  );

  const save = React.useCallback(async () => {
    if (!isBusinessCustomerMailStatus(status)) {
      alert(`Invalid status ${status}`);
      throw Error("invalid status");
    }

    if (!!templateSuffix && templateSuffix.trim() !== "-se") {
      alert(`Invalid templateSuffix ${status}`);
      throw Error("invalid status");
    }

    // Existing
    if (mailData.uid) {
      const model = businessCustomerMailsStore.byId.get(mailData.uid);
      if (!model) {
        throw Error("Mail model not found");
      }

      await model.save({
        companyName,
        email,
        status,
        currentStep,
        templateSuffix,
      });
    } else {
      if (businessCustomerMailsStore.all.find((b) => b.data.email === email)) {
        alert("Duplicate email address");
        throw Error("Duplicate email address");
      }

      await businessCustomerMailsApi.add({
        ...mailData,
        status,
        companyName,
        email,
        templateSuffix,
      });
    }
  }, [
    businessCustomerMailsStore,
    businessCustomerMailsApi,
    mailData,
    companyName,
    currentStep,
    templateSuffix,
    email,
    status,
  ]);

  return (
    <>
      <FormInput
        label={"Name"}
        defaultValue={companyName}
        type="border"
        onChangeText={setCompanyName}
        placeholder={"Name"}
      />

      <FormInput
        label={"Email"}
        defaultValue={email}
        type="border"
        onChangeText={setEmail}
        placeholder={"Email"}
      />

      <FormInput
        label={"Step"}
        defaultValue={`${currentStep}`}
        onChangeText={(t) => {
          setCurrentStep(Number.parseInt(t, 10));
        }}
        type="border"
        placeholder={"Step"}
      />

      <FormInput
        label={"Status"}
        defaultValue={status}
        onChangeText={(t) => {
          setStatus(t);
        }}
        type="border"
        placeholder={"Status"}
      />

      <FormInput
        label={"TemplateSuffix"}
        defaultValue={templateSuffix}
        onChangeText={(t) => {
          setTemplateSuffix(t);
        }}
        type="border"
        placeholder={"-se or empty"}
      />

      <Button
        onPress={async () => {
          await save();
          onSave();
        }}
      >
        Save Mail Item
      </Button>
    </>
  );
};

const MailRow: React.FC<{
  mailData: BusinessCustomerMailModel;
  setEditMail: (data: FormData) => void;
}> = observer(({ mailData, setEditMail }) => {
  const infoRowProps = useMemo<InfoRowItemProps[]>(() => {
    return [
      {
        icon: Pen,
        text: (
          <Text
            onPress={() => {
              setEditMail(mailData.data);
            }}
            cursor="pointer"
          >
            Edit
          </Text>
        ),
      },
      {
        text: mailData.uid,
      },
    ];
  }, [mailData, setEditMail]);

  const [showComments, setShowComments] = React.useState(false);

  return (
    <YStack gap={"$medium"} width={"100%"}>
      <XStack
        gap={"$medium"}
        width={"100%"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
      >
        <YStack gap={"$medium"}>
          <Label>{mailData.data.companyName}</Label>
          <Label>{mailData.data.email}</Label>
          <XStack gap={"$medium"}>
            <Label>{mailData.data.status}</Label>
            <Label>Step {mailData.data.currentStep}</Label>
            {mailData.data.lastEmailSent && (
              <Label>
                Mail sent{" "}
                {getDayJsFromDate(new Date(mailData.data.lastEmailSent))}
              </Label>
            )}
          </XStack>
          <InfoRow items={infoRowProps} />
        </YStack>

        <YStack gap={"$medium"}>
          <Button
            iconPosition={"before"}
            icon={MessageCircle}
            type={"secondary"}
            onPress={() => {
              setShowComments(!showComments);
            }}
          >
            {mailData.data.comments.length} comments
          </Button>
          <XStack gap={"$small"}>
            <Body muted>{mailData.data.opened} open</Body>
            <Body muted>{mailData.data.clicked} click</Body>
          </XStack>

          {mailData.data.status !== BusinessCustomerMailStatus.Aborted && (
            <Button
              type="ghost"
              onPress={() => {
                mailData.updateStatus(BusinessCustomerMailStatus.Aborted);
              }}
            >
              Abort
            </Button>
          )}
        </YStack>
      </XStack>

      {showComments && (
        <Box backgroundColor={"$backgroundMutedBlock"}>
          <Label>Comments</Label>

          <Group>
            {mailData.data.comments.map((comment, index) => (
              <YStack gap={"$small"} key={index}>
                <Body muted>
                  {getDayJsFromDate(new Date(comment.dateCreated))}
                </Body>
                <Label>{comment.authorName}</Label>
                <Body>{comment.text}</Body>
              </YStack>
            ))}
          </Group>

          <Input
            type="border"
            placeholder={"Add comment..."}
            submitOnEnter={false}
            submitOnBlur={false}
            blurOnSubmit={false}
            icon={Send}
            clearOnSubmit
            onSubmitEditing={(e) => {
              mailData.addComment(e.nativeEvent.text);
            }}
          />
        </Box>
      )}
    </YStack>
  );
});

export async function runInBatches<T>(
  items: T[],
  batchSize: number,
  delayMs: number,
  task: (item: T) => Promise<void>,
) {
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);

    // Run the task for each item in the batch
    await Promise.all(batch.map((item) => task(item)));

    // Wait before the next batch (skip waiting after the last one)
    if (i + batchSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

enum SortOrder {
  Status = "status",
  Clicks = "clicks",
  Opens = "opens",
  Step = "steps",
}

export const BusinessCustomerMailsUI = observer(() => {
  const { businessCustomerMailsApi } = useApi();
  const { businessCustomerMailsStore } = useStores();
  const [isLoading, setIsLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");

  const [sortOrder, setSortOrder] = React.useState(SortOrder.Status);

  React.useEffect(() => {
    void businessCustomerMailsApi.getAllMails().then(() => setIsLoading(false));
  }, [businessCustomerMailsApi]);

  const items: BusinessCustomerMailModel[] = React.useMemo(() => {
    console.info("Sorting items...");
    let sortItems = businessCustomerMailsStore.all.slice();

    if (filter) {
      sortItems = sortItems.filter(
        (item) =>
          item.data.companyName.toLowerCase().includes(filter) ||
          item.data.email.toLowerCase().includes(filter),
      );
    }

    if (sortOrder === SortOrder.Status) {
      return sortItems.sort((a, b) => (a.data.status < b.data.status ? -1 : 1));
    }

    if (sortOrder === SortOrder.Clicks) {
      return sortItems.sort((a, b) =>
        a.data.clicked < b.data.clicked ? 1 : -1,
      );
    }

    if (sortOrder === SortOrder.Opens) {
      return sortItems.sort((a, b) => (a.data.opened < b.data.opened ? 1 : -1));
    }

    if (sortOrder === SortOrder.Step) {
      return sortItems.sort((a, b) =>
        a.data.currentStep < b.data.currentStep ? -1 : 1,
      );
    }

    return sortItems;
  }, [businessCustomerMailsStore, sortOrder, filter]);

  const isActiveCount = items.filter((item) => item.isActive).length;

  const [showModal, setShowModal] = React.useState(false);
  const [editMail, setEditMail] = React.useState<FormData | null>(null);

  return (
    <YStack gap={"$medium"}>
      <Label>
        Mails (Active {isActiveCount}/{items.length})
      </Label>

      <XStack gap={"$medium"} width={"100%"}>
        <Button
          flex={1}
          onPress={() => {
            setShowModal(true);
          }}
        >
          Import Mails
        </Button>

        <Button
          flex={1}
          onPress={() => {
            setEditMail({
              status: BusinessCustomerMailStatus.NotStarted,
              companyName: "",
              email: "",
              opened: 0,
              clicked: 0,
              templateSuffix: "",
              currentStep: 1,
              lastEmailSent: null,
              history: [],
              comments: [],
            });
          }}
        >
          Add Mail Recipient
        </Button>
      </XStack>

      <XStack gap={"$medium"} width={"100%"}>
        <Button
          flex={1}
          onPress={async () => {
            const activeItems = items.filter(
              (m) => m.data.status === BusinessCustomerMailStatus.Active,
            );

            await runInBatches(
              activeItems,
              99, // batch size
              1000, // 1 second delay
              (m) => m.updateStatus(BusinessCustomerMailStatus.Paused),
            );
          }}
        >
          Set Active -{">"} Paused
        </Button>

        <Button
          flex={1}
          onPress={async () => {
            const activeItems = items.filter(
              (m) => m.data.status === BusinessCustomerMailStatus.Paused,
            );

            await runInBatches(
              activeItems,
              99, // batch size
              1000, // 1 second delay
              (m) => m.updateStatus(BusinessCustomerMailStatus.Active),
            );
          }}
        >
          Set Paused -{">"} Active
        </Button>
      </XStack>

      <XStack gap={"$medium"} width={"100%"} alignItems={"center"}>
        <Body muted>Sort by</Body>
        <Button
          type={sortOrder === SortOrder.Status ? "primary" : "secondary"}
          onPress={() => {
            setSortOrder(SortOrder.Status);
          }}
        >
          Status
        </Button>
        <Button
          type={sortOrder === SortOrder.Opens ? "primary" : "secondary"}
          onPress={() => {
            setSortOrder(SortOrder.Opens);
          }}
        >
          Open
        </Button>
        <Button
          type={sortOrder === SortOrder.Clicks ? "primary" : "secondary"}
          onPress={() => {
            setSortOrder(SortOrder.Clicks);
          }}
        >
          Click
        </Button>
        <Button
          type={sortOrder === SortOrder.Step ? "primary" : "secondary"}
          onPress={() => {
            setSortOrder(SortOrder.Step);
          }}
        >
          Step
        </Button>
      </XStack>

      <Input
        value={filter}
        type="border"
        submitOnEnter={false}
        submitOnBlur={false}
        blurOnSubmit={false}
        icon={Filter}
        onChangeText={(t) => {
          setFilter(t);
        }}
        placeholder={"Filter list"}
      />
      {isLoading && (
        <View alignSelf={"center"} margin={"$medium"}>
          <LoadingCircular />
        </View>
      )}

      {!!editMail && (
        <CustomerEdit
          mailData={editMail}
          onSave={() => {
            setEditMail(null);
          }}
        />
      )}

      <Group>
        {items.map((item) => (
          <MailRow key={item.uid} mailData={item} setEditMail={setEditMail} />
        ))}
      </Group>

      <BusinessPlanImportMailsModal
        onClose={() => setShowModal(false)}
        visible={showModal}
      />
    </YStack>
  );
});
